import { X } from "lucide-solid";
import { createEffect, createSignal } from "solid-js";
import { Portal, Show } from "solid-js/web";
import Eventor from "~/js/utilities/Eventor";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { Button, isUserFree, useUserCredits } from "~/solidJs/shared";
import {
	createBuyCreditsProps,
	createPricingProps
} from "~/solidJs/shared/models/pageParams/pricingPageParams";
import {
	showToastMessage,
	ToastMessageTypes
} from "~/solidJs/widgets/WorkspaceToast/WorkspaceToast";

export const UserCreditsEventor = new Eventor();

export enum UserCreditsEvent {
	UPDATE = "update"
}

export const UserCreditsTracker = () => {
	const userCredits = useUserCredits();

	createEffect(() => {
		const credits = userCredits.remainCredits();
		UserCreditsEventor.invoke(UserCreditsEvent.UPDATE, credits);
	});

	return null;
};

const CreditsToasContent = () => {
	return (
		<div class="flex items-center justify-center gap-4">
			<span class="caption-12-semibold">Oops, you're low on credits!</span>
			<Button
				component="a"
				target="_blank"
				{...(isUserFree(getUserStore())
					? createPricingProps("low_on_credits_toast")
					: createBuyCreditsProps("low_on_credits_toast"))}
				class="bg-[rgb(255,255,255)]/20 text-white hover:bg-[rgb(255,255,255)]/30"
				size="small"
			>
				Get more
			</Button>
		</div>
	);
};

export const showInsufficientCreditsToast = () => {
	showToastMessage({
		children: <CreditsToasContent />,
		timeout: 5000,
		type: ToastMessageTypes.Warning,
		containerClass: "p-2 pl-5"
	});
};

export const LowCreditsToast = () => {
	const [getIsLowCredits, setIsLowCredits] = createSignal(false);
	let hasBannerBeenShown = false;
	const credits = useUserCredits();

	createEffect(() => {
		if (hasBannerBeenShown) return;
		const total = credits.totalCredits();
		const balance = credits.remainCredits();
		const isLow = balance / total < 0.1;
		if (isLow) {
			setIsLowCredits(hasBannerBeenShown === false);
			hasBannerBeenShown = true;
		}
	});

	return (
		<Show when={getIsLowCredits()}>
			<Portal>
				<div class="absolute right-2 bottom-14 z-[999] flex items-center justify-center gap-2.5 rounded-lg bg-white p-2 pl-3">
					<span class="caption-11-medium whitespace-pre-wrap text-text-primary">
						You're at 90%! Top up your credits now to keep{"\n"}creating without limits!
					</span>
					<Button
						component="a"
						target="_blank"
						{...(isUserFree(getUserStore())
							? createPricingProps("90_low_toast")
							: createBuyCreditsProps("90_low_toast"))}
						variant="actionviolent"
						size="small"
					>
						Get more
					</Button>
					<Button
						onClick={() => setIsLowCredits(false)}
						variant="AccentIcon"
						class="rounded-none p-0"
					>
						<X size={12} />
					</Button>
				</div>
			</Portal>
		</Show>
	);
};
